import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LanguageSelector } from '@/components/ui/language-selector';
import { AddPresentationVideoDialog } from '@/components/presentation/AddPresentationVideoDialog';
import { EditPresentationVideoDialog } from '@/components/presentation/EditPresentationVideoDialog';
import { DeleteVideoDialog } from '@/components/presentation/DeleteVideoDialog';
import { SortableVideoGrid } from '@/components/presentation/SortableVideoGrid';
import { ArrowLeft, Plus, LogIn, Play } from 'lucide-react';
import Footer from '@/components/layout/Footer';
import logoGolf from "@/assets/logo-golf.webp";
import SEO from "@/components/seo/SEO";

interface PresentationVideo {
  id: string;
  title: string;
  description: string | null;
  youtube_url: string;
  category: string;
  display_order: number;
}

const categories = ['features', 'tutorials', 'testimonials', 'news'] as const;

const Presentation = () => {
  const { t } = useTranslation('presentation');
  const navigate = useNavigate();
  const { isAdmin } = useIsAdmin();
  const [videos, setVideos] = useState<PresentationVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<PresentationVideo | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('features');

  const fetchVideos = async () => {
    try {
      const { data, error } = await supabase
        .from('presentation_videos')
        .select('id, title, description, youtube_url, category, display_order')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setVideos(data || []);
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  const getVideosByCategory = (cat: string) => videos.filter(v => v.category === cat);

  const handleReorder = (category: string, reorderedVideos: PresentationVideo[]) => {
    setVideos(prev => {
      const otherVideos = prev.filter(v => v.category !== category);
      return [...otherVideos, ...reorderedVideos].sort((a, b) => a.display_order - b.display_order);
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white flex flex-col">
      <SEO
        title="Présentation vidéo — Golf Progress Calculator"
        description="Découvrez Golf Progress Calculator en vidéo : fonctionnalités, tutoriels et témoignages pour suivre et améliorer votre golf."
        path="/presentation"
        jsonLd={videos.slice(0, 10).map((v) => ({
          "@context": "https://schema.org",
          "@type": "VideoObject",
          name: v.title,
          description: v.description || v.title,
          embedUrl: v.youtube_url,
          contentUrl: v.youtube_url
        }))}
      />
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-sm z-10 border-b">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/auth')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <img 
              src={logoGolf} 
              alt="Logo Golf" 
              className="h-10 w-auto"
              width="40"
              height="40"
              fetchPriority="high"
            />

          </div>
          <div className="flex items-center gap-2">
            <LanguageSelector />
            <Button onClick={() => navigate('/auth')} className="bg-golf-500 hover:bg-golf-600">
              <LogIn className="mr-2 h-4 w-4" />
              {t('buttons.login')}
            </Button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 pt-24 pb-8 flex-1">
        {/* Hero section */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t('title')}</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('subtitle')}</p>
          
          {isAdmin && (
            <Button 
              onClick={() => setIsAddDialogOpen(true)} 
              className="mt-4 bg-golf-500 hover:bg-golf-600"
            >
              <Plus className="mr-2 h-4 w-4" />
              {t('buttons.addVideo')}
            </Button>
          )}
        </div>

        {/* Videos by category */}
        <Tabs value={activeCategory} onValueChange={setActiveCategory} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            {categories.map((cat) => (
              <TabsTrigger key={cat} value={cat} className="text-xs sm:text-sm">
                {t(`categories.${cat}`)}
              </TabsTrigger>
            ))}
          </TabsList>

          {categories.map((cat) => {
            const categoryVideos = getVideosByCategory(cat);
            return (
              <TabsContent key={cat} value={cat}>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-golf-500" />
                  </div>
                ) : categoryVideos.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Play className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>{t('noVideos')}</p>
                  </div>
                ) : (
                  <SortableVideoGrid
                    videos={categoryVideos}
                    isAdmin={isAdmin}
                    onEdit={(video) => {
                      setSelectedVideo(video);
                      setIsEditDialogOpen(true);
                    }}
                    onDelete={(video) => {
                      setSelectedVideo(video);
                      setIsDeleteDialogOpen(true);
                    }}
                    onReorder={(reorderedVideos) => handleReorder(cat, reorderedVideos)}
                  />
                )}
              </TabsContent>
            );
          })}
        </Tabs>

      </div>

      <Footer />

      <AddPresentationVideoDialog
        isOpen={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onSuccess={fetchVideos}
      />

      <EditPresentationVideoDialog
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onSuccess={fetchVideos}
        video={selectedVideo}
      />

      <DeleteVideoDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onSuccess={fetchVideos}
        video={selectedVideo}
      />
    </div>
  );
};

export default Presentation;
